import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
} from 'recharts';
import {
  CATEGORY_COLORS,
  EVALUATION_AXES,
  EVALUATION_RADAR_COLOR,
  EVALUATION_RADIUS_DOMAIN,
  RADAR_PREV_COLOR,
  RESOLUTION_AXES,
  RESOLUTION_RADAR_COLOR,
  RESOLUTION_RADIUS_DOMAIN,
  type AxisDef,
  type EvalSeriesPoint,
} from './designEvaluation.js';
import {
  CategoryLegend,
  EvaluationScorePanel,
  ResolutionScorePanel,
} from './DesignEvaluationScorePanels.js';

export interface DesignEvaluationRadarProps {
  /** 選択期間 (当月/当Q) の評価。null なら描画しない */
  selected: EvalSeriesPoint | null;
  /** 前期間 (= 灰のベースライン) の評価。null なら前期間線を引かない */
  prev: EvalSeriesPoint | null;
  /** 選択期間の表示ラベル (例: "2026年6月" / "2026/4Q") */
  selectedLabel: string;
  /** 前期間の表示ラベル。null なら前期間なし */
  prevLabel: string | null;
}

/**
 * 軸キー → 値 のレコード (1 系列ぶん)。`Partial` なのは、呼び出し側が軸の部分集合しか
 * 持たない期間 (未評価の軸がある月) をそのまま渡せるようにするため。
 * @graph-connects none
 */
export type AxisValues<K extends string> = Partial<Record<K, number | null>>;

interface RadarRow {
  axis: string;
  current: number | null;
  prev: number | null;
  /** 任意の第 3 系列 (個人 MyPage の「前期間の個人評価」参照線) */
  extra: number | null;
}

/** 【】接頭辞を取り除く (PJ解像度の軸ラベルは分類を色で示すためテキストからは外す)。 @graph-connects none */
function stripBracket(label: string): string {
  return label.replace(/^【[^】]*】/, '');
}

/**
 * 軸ラベルを読みやすく 2 行に折り返す。8 文字以下は 1 行。長い場合は中央付近の
 * 自然な区切り (助詞・「・」「ー」の直後) で改行し、無ければ中央で均等分割する。
 * @graph-connects none
 */
function wrapAxisLabel(label: string): string[] {
  const MAX = 8;
  if (label.length <= MAX) return [label];
  // 句点的に区切ると自然な文字の直後で改行する (「Howに基づいた / ラフ詳細」等)。
  const breakAfter = new Set(['・', 'ー', 'の', 'た', '、']);
  const mid = label.length / 2;
  let best = -1;
  let bestDist = Infinity;
  for (let i = 1; i < label.length - 1; i++) {
    if (breakAfter.has(label[i] ?? '')) {
      const dist = Math.abs(i + 1 - mid);
      if (dist < bestDist) {
        bestDist = dist;
        best = i + 1;
      }
    }
  }
  const cut = best > 0 ? best : Math.ceil(mid);
  return [label.slice(0, cut), label.slice(cut)];
}

/** 軸ラベルを円から外へ押し出す距離 (px)。 @graph-connects none */
const LABEL_OUTSET = 12;

interface AngleTickProps {
  x?: number | string;
  y?: number | string;
  cx?: number | string;
  cy?: number | string;
  payload?: { value?: string | number };
  /** ラベル文字列 → 色 (分類色)。未指定は labelColor or ミュート色 */
  colorByLabel?: Record<string, string>;
  /** ラベル文字列 → 件数注記 (例 "（3件）")。ラベル下に小さく添える */
  countByLabel?: Record<string, string>;
  /** ラベル文字列 → 差分注記 (例 "+0.02" + 符号別の色)。件数注記の下に添える */
  diffByLabel?: Record<string, { text: string; color: string }>;
  /** 分類色を持たない軸ラベル / 件数注記の既定色 (未指定はミュート色) */
  labelColor?: string;
}

/** 角度軸ラベルを構成する 1 行 (本体ラベル / 件数 / 差分)。 @graph-connects none */
interface LabelPart {
  key: string;
  text: string;
  fontSize?: number;
  fontWeight?: number;
  fill?: string;
}

/** 多行 + 分類着色 + (任意で件数 / 差分注記) + 円から少し離して配置する角度軸ラベル。 @graph-connects none */
function AngleTick({
  x = 0,
  y = 0,
  cx = 0,
  cy = 0,
  payload,
  colorByLabel,
  countByLabel,
  diffByLabel,
  labelColor,
}: AngleTickProps) {
  const value = String(payload?.value ?? '');
  const px = Number(x);
  const py = Number(y);
  const cxNum = Number(cx);
  const cyNum = Number(cy);
  // 中心→ラベルの向きへ LABEL_OUTSET だけ押し出して円から離す。
  const dx = px - cxNum;
  const dy = py - cyNum;
  const len = Math.hypot(dx, dy) || 1;
  const ox = px + (dx / len) * LABEL_OUTSET;
  const oy = py + (dy / len) * LABEL_OUTSET;
  const lines = wrapAxisLabel(value);
  const anchor = px > cxNum + 4 ? 'start' : px < cxNum - 4 ? 'end' : 'middle';
  const fill = colorByLabel?.[value] ?? labelColor ?? 'var(--color-text-muted)';
  const countText = countByLabel?.[value];
  const diff = diffByLabel?.[value];

  const labelParts: LabelPart[] = lines.map((ln, i) => ({ key: `l${String(i)}`, text: ln }));
  const countParts: LabelPart[] =
    countText != null
      ? [
          {
            key: 'count',
            text: countText,
            fontSize: 9,
            fill: labelColor ?? 'var(--color-text-muted)',
          },
        ]
      : [];
  const diffParts: LabelPart[] =
    diff != null
      ? [{ key: 'diff', text: diff.text, fontSize: 13, fontWeight: 700, fill: diff.color }]
      : [];
  // 差分はラベルの「外側」(円から遠い側) に置く。上半分の軸は上、下半分の軸は下へ出す。
  const isUpper = py < cyNum;
  const parts =
    diff != null && isUpper
      ? [...diffParts, ...labelParts, ...countParts]
      : [...labelParts, ...countParts, ...diffParts];
  // 総行数を点に対し縦中央へ寄せる。
  const baseDy = parts.length > 1 ? -((parts.length - 1) * 6) : 0;
  // 差分は軸ラベルから少し離す: 上軸は差分の次行 (=ラベル) を、下軸は差分行自身を広めに空ける。
  const diffIdx = parts.findIndex((p) => p.key === 'diff');
  const DIFF_GAP = 7;
  const dyFor = (i: number): number => {
    if (i === 0) return baseDy;
    if (diffIdx >= 0 && (isUpper ? i === diffIdx + 1 : i === diffIdx)) return 12 + DIFF_GAP;
    return 12;
  };
  return (
    <text x={ox} y={oy} textAnchor={anchor} fill={fill} fontSize={10}>
      {parts.map((p, i) => (
        <tspan
          key={p.key}
          x={ox}
          dy={dyFor(i)}
          fontSize={p.fontSize}
          fontWeight={p.fontWeight}
          fill={p.fill}
        >
          {p.text}
        </tspan>
      ))}
    </text>
  );
}

interface SmoothShapeProps {
  points?: { x: number; y: number }[];
  stroke?: string;
  fill?: string;
  fillOpacity?: number;
  strokeWidth?: number;
}

/**
 * radar のポリゴンを「閉じた Catmull-Rom スプライン」で柔らかい曲線にする custom shape。
 * 直線の多角形ではなく折線図と同様のなめらかな線にする。
 * @graph-connects none
 */
function SmoothRadarShape({ points, stroke, fill, fillOpacity, strokeWidth }: SmoothShapeProps) {
  if (!points || points.length < 3) return null;
  const n = points.length;
  const pt = (i: number) => points[((i % n) + n) % n] as { x: number; y: number };
  let d = `M ${String(pt(0).x)},${String(pt(0).y)} `;
  for (let i = 0; i < n; i++) {
    const p0 = pt(i - 1);
    const p1 = pt(i);
    const p2 = pt(i + 1);
    const p3 = pt(i + 2);
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += `C ${String(c1x)},${String(c1y)} ${String(c2x)},${String(c2y)} ${String(p2.x)},${String(p2.y)} `;
  }
  d += 'Z';
  return (
    <path
      d={d}
      stroke={stroke}
      strokeWidth={strokeWidth}
      fill={fill}
      fillOpacity={fillOpacity}
      strokeLinejoin="round"
    />
  );
}

interface RadarTooltipPayload {
  payload?: RadarRow;
}

/** @graph-connects none */
function RadarTooltip(props: {
  active?: boolean;
  payload?: RadarTooltipPayload[];
  selectedLabel: string;
  prevLabel: string | null;
  currentColor: string;
  extraLabel?: string | null;
  extraColor?: string;
}) {
  if (!props.active || !props.payload || props.payload.length === 0) return null;
  const row = props.payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-lg text-xs">
      <div className="text-[var(--color-text-muted)] mb-1">{row.axis}</div>
      <div className="tabular-nums" style={{ color: props.currentColor }}>
        {props.selectedLabel}: {row.current == null ? '—' : row.current.toFixed(2)}
      </div>
      {props.extraLabel != null && (
        <div className="tabular-nums" style={{ color: props.extraColor ?? RADAR_PREV_COLOR }}>
          {props.extraLabel}: {row.extra == null ? '—' : row.extra.toFixed(2)}
        </div>
      )}
      {props.prevLabel != null && (
        <div className="tabular-nums" style={{ color: RADAR_PREV_COLOR }}>
          {props.prevLabel}: {row.prev == null ? '—' : row.prev.toFixed(2)}
        </div>
      )}
    </div>
  );
}

/**
 * 1 つの radar。軸定義と**軸キー → 値のレコード**だけを受ける汎用コンポーネント。
 *
 * 系列を「評価点オブジェクト + どのフィールドを見るか」ではなく値レコードで受けるのは、
 * デザイン評価 (PJ解像度 5 軸 / チーム貢献度 3 軸) と DLE 評価 (4 軸) が同じ描画を
 * 共有するため。呼び出し側が `point.evaluation` のように必要な面だけ切り出して渡す。
 * @graph-connects none
 */
export function EvalRadar<K extends string>({
  axes,
  selected,
  prev,
  height,
  domain,
  currentColor,
  selectedLabel,
  prevLabel,
  axisCounts,
  axisLabelColor,
  extra,
  extraColor,
  extraLabel,
  showDiff,
  diffPositiveColor,
}: {
  axes: readonly AxisDef<K>[];
  /** 当期間の軸別値。null なら当期間線を引かない */
  selected: AxisValues<K> | null;
  /** 前期間 (= 灰のベースライン) の軸別値。null なら前期間線を引かない */
  prev: AxisValues<K> | null;
  height: number;
  /** 半径スケール ([0, 満点])。解像度=3 / 貢献度=5 */
  domain: [number, number];
  /** 当期間系列色 (解像度=teal / 貢献度=橙) */
  currentColor: string;
  /** 当期間ラベル */
  selectedLabel: string;
  /** 前期間ラベル */
  prevLabel: string | null;
  /** 軸キー → 評価件数。指定すると各軸ラベル下に「（X件）」を添える (karte の個人 radar 用) */
  axisCounts?: Partial<Record<K, number | null>>;
  /** 分類色を持たない軸ラベルの色 (未指定はミュート色)。dark mode 視認性向上に使う */
  axisLabelColor?: string;
  /** 任意の第 3 系列 (個人 MyPage の「前期間の個人評価」参照線)。null なら描かない */
  extra?: AxisValues<K> | null;
  /** 第 3 系列の色 (当期間色の淡色)。 */
  extraColor?: string;
  /** 第 3 系列のラベル。null なら凡例・tooltip に出さない */
  extraLabel?: string | null;
  /** 各軸ラベルの外側に「当期間 − 前期間(灰ベースライン)」の差分を表示する (個人 vs 全体差分用) */
  showDiff?: boolean;
  /** 差分が正のときの色 (未指定は当期間色)。負・ゼロはミュート色 */
  diffPositiveColor?: string;
}) {
  const { t } = useTranslation();

  // 軸ラベルは【】を外す。分類 (category) があればラベル→分類色のマップを作る。
  // axisCounts があればラベル→「（X件）」の注記、showDiff ならラベル→差分注記マップも作る。
  const { data, colorByLabel, countByLabel, diffByLabel } = useMemo(() => {
    const colors: Record<string, string> = {};
    const counts: Record<string, string> = {};
    const diffs: Record<string, { text: string; color: string }> = {};
    const rows = axes.map((a) => {
      const label = stripBracket(t(a.labelKey));
      if (a.category) colors[label] = CATEGORY_COLORS[a.category];
      const cnt = axisCounts?.[a.key];
      if (cnt != null) counts[label] = `（${String(cnt)}件）`;
      const cur = selected?.[a.key] ?? null;
      const pv = prev?.[a.key] ?? null;
      if (showDiff && cur != null && pv != null) {
        const d = cur - pv;
        const sign = d > 0 ? '+' : d < 0 ? '-' : '±';
        diffs[label] = {
          text: `${sign}${Math.abs(d).toFixed(2)}`,
          color: d > 0 ? (diffPositiveColor ?? currentColor) : 'var(--color-text-muted)',
        };
      }
      return {
        axis: label,
        current: cur,
        prev: pv,
        extra: extra?.[a.key] ?? null,
      };
    });
    return { data: rows, colorByLabel: colors, countByLabel: counts, diffByLabel: diffs };
  }, [axes, selected, prev, extra, t, axisCounts, showDiff, diffPositiveColor, currentColor]);

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <RadarChart
          data={data}
          margin={{ top: 36, right: 88, bottom: 36, left: 88 }}
          outerRadius="78%"
        >
          <PolarGrid stroke="var(--color-border)" gridType="circle" />
          <PolarAngleAxis
            dataKey="axis"
            tick={(p) => (
              <AngleTick
                {...p}
                colorByLabel={colorByLabel}
                countByLabel={countByLabel}
                diffByLabel={diffByLabel}
                labelColor={axisLabelColor}
              />
            )}
          />
          <PolarRadiusAxis
            domain={domain}
            tickCount={domain[1] - domain[0] + 1}
            tick={{ fill: 'var(--color-text-muted)', fontSize: 9 }}
            axisLine={false}
            angle={90}
          />
          {prev != null && (
            <Radar
              name="prev"
              dataKey="prev"
              stroke={RADAR_PREV_COLOR}
              fill={RADAR_PREV_COLOR}
              fillOpacity={0.1}
              strokeWidth={1.5}
              shape={<SmoothRadarShape />}
              isAnimationActive={false}
            />
          )}
          {extra != null && (
            <Radar
              name="extra"
              dataKey="extra"
              stroke={extraColor ?? RADAR_PREV_COLOR}
              fill={extraColor ?? RADAR_PREV_COLOR}
              fillOpacity={0.08}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              shape={<SmoothRadarShape />}
              isAnimationActive={false}
            />
          )}
          <Radar
            name="current"
            dataKey="current"
            stroke={currentColor}
            fill={currentColor}
            fillOpacity={0.22}
            strokeWidth={2.25}
            shape={<SmoothRadarShape />}
            isAnimationActive
            animationDuration={700}
          />
          <RTooltip
            content={
              <RadarTooltip
                selectedLabel={selectedLabel}
                prevLabel={prevLabel}
                currentColor={currentColor}
                extraLabel={extraLabel}
                extraColor={extraColor}
              />
            }
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** 当月 (色) / 前月 (灰) の凡例。karte モーダルでも再利用する。 @graph-connects none */
export function MonthLegend({
  selectedLabel,
  prevLabel,
  currentColor,
}: {
  selectedLabel: string;
  prevLabel: string | null;
  currentColor: string;
}) {
  return (
    <div className="flex items-center justify-center gap-4 text-xs text-[var(--color-text-muted)]">
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block w-3 h-0.5 rounded"
          style={{ background: currentColor }}
          aria-hidden="true"
        />
        {selectedLabel}
      </div>
      {prevLabel != null && (
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-0.5 rounded"
            style={{ background: RADAR_PREV_COLOR }}
            aria-hidden="true"
          />
          {prevLabel}
        </div>
      )}
    </div>
  );
}

/**
 * 全体の当月評価を radar で表示する。PJ解像度 (五角形, 当月=teal) と チーム貢献度評価
 * (三角形, 当月=橙) の 2 枚を並べ、前月を灰のベースラインとして重ね当月/前月差を可視化する。
 * 線は折線図と同様のなめらかな閉曲線。解像度の軸ラベルは企画=青 / 設計=桃 で色分けする。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン評価カードの radar (PJ解像度五角形 + 貢献度三角形, 前月比較)
 * @graph-connects none
 */
export function DesignEvaluationRadar({
  selected,
  prev,
  selectedLabel,
  prevLabel,
}: DesignEvaluationRadarProps) {
  const { t } = useTranslation();

  if (!selected) {
    return (
      <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
        {t('idtKpi.designEvaluation.empty')}
      </div>
    );
  }

  return (
    // radar 2 枚は本番どおり max-w-[1040px] の中央寄せグリッドに戻す。
    // スコア表はグリッド外の relative ラッパー右端 (= カード右端) に絶対配置し、
    // 貢献度 radar の縦中央に合わせる (radar の位置はそのまま)。
    // @container でカード実幅を基準に判定する。viewport の lg だとサイドバー展開時に
    // カード幅が縮んでもスコア表の絶対配置が維持され radar と重なる。スコア表を radar 左右に
    // 絶対配置できる十分な幅 (≈1280px) がある時だけ横配置にし、足りなければ radar 下へ積む。
    <div className="relative @container">
      <div className="grid grid-cols-1 @[760px]:grid-cols-2 gap-6 @[1280px]:max-w-[1040px] @[1280px]:mx-auto">
        <div className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] text-center">
            {t('idtKpi.designEvaluation.resolutionTitle')}
          </h4>
          <EvalRadar
            axes={RESOLUTION_AXES}
            selected={selected.resolution}
            prev={prev?.resolution ?? null}
            height={460}
            domain={RESOLUTION_RADIUS_DOMAIN}
            currentColor={RESOLUTION_RADAR_COLOR}
            selectedLabel={selectedLabel}
            prevLabel={prevLabel}
          />
          {/* 分類凡例 (企画/設計) を radar 直下・日付凡例の上に横並びで置く */}
          <CategoryLegend />
          <MonthLegend
            selectedLabel={selectedLabel}
            prevLabel={prevLabel}
            currentColor={RESOLUTION_RADAR_COLOR}
          />
          {/* 狭幅: 表は解像度 radar の下に置く (横に絶対配置する幅が無いとき) */}
          <div className="mt-1 flex justify-center @[1280px]:hidden">
            <ResolutionScorePanel selected={selected} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] text-center">
            {t('idtKpi.designEvaluation.evaluationTitle')}
          </h4>
          <EvalRadar
            axes={EVALUATION_AXES}
            selected={selected.evaluation}
            prev={prev?.evaluation ?? null}
            height={460}
            domain={EVALUATION_RADIUS_DOMAIN}
            currentColor={EVALUATION_RADAR_COLOR}
            selectedLabel={selectedLabel}
            prevLabel={prevLabel}
          />
          <MonthLegend
            selectedLabel={selectedLabel}
            prevLabel={prevLabel}
            currentColor={EVALUATION_RADAR_COLOR}
          />
          {/* 狭幅: 表は貢献度 radar の下に置く (横に絶対配置する幅が無いとき) */}
          <div className="mt-1 flex justify-center @[1280px]:hidden">
            <EvaluationScorePanel selected={selected} />
          </div>
        </div>
      </div>
      {/* デスクトップ: 解像度 radar の円の左端 (≈中央-400px) からカード左端までの空白の中央に
          鏡像の表を置く (右の評価表の左右対称)。 */}
      <div
        className="absolute hidden items-center justify-center @[1280px]:flex"
        style={{ top: 24, height: 460, left: -24, right: 'calc(50% + 480px)' }}
      >
        <ResolutionScorePanel selected={selected} />
      </div>
      {/* デスクトップ: 貢献度 radar の円の右端 (≈中央+395px) からカード右端までの空白の中央に表を置く。
          貢献度 radar (top 24, height 460) の縦中央に合わせる。 */}
      <div
        className="absolute hidden items-center justify-center @[1280px]:flex"
        style={{ top: 24, height: 460, left: 'calc(50% + 400px)', right: -24 }}
      >
        <EvaluationScorePanel selected={selected} />
      </div>
    </div>
  );
}
